import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { saveMitigationResponse, clearMitigationResponse } from 'stores/modules/cap/selfassessmentreducer';
import { useLocation, useNavigate } from 'react-router-dom';

import { useNotification } from 'config/Notification';
import LoadingOverlay from 'components/LoadingOverlay.jsx';

import '../../styles/cap.css';

import { fetchAssessmentReportdetailsforReview as fetchAssessmentReportDetailsforReviewService } from 'services/modules/cap/standardservice';
import {
  updateMultipleMitigationResponses as updateMultipleMitigationResponsesService,
  putAssessmentStatus as putAssessmentStatusService,
} from 'services/modules/cap/assessmentservice';

import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import QuestionRenderer from '../../components/shared/QuestionRenderer';

const SubmitMitigationResponsePage = () => {

  // State variables
  const [assessmentsList, setAssessmentsList] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mitigation, setMitigation] = useState({});
  const [mitigationRejection, setMitigationRejection] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ── Details dialog state ──────────────────────────────────────────────────
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSubcontrol, setSelectedSubcontrol] = useState(null);

  const openDetailDialog = (subcontrol) => {
    setSelectedSubcontrol(subcontrol);
    setDetailDialog(true);
  };

  const closeDetailDialog = () => {
    setDetailDialog(false);
    setSelectedSubcontrol(null);
  };

  // React routers
  const navigate = useNavigate();
  const location = useLocation();
  const urlparams = location.state || {};

  // Notification
  const { openNotification } = useNotification();

  const dispatch = useDispatch();
  const mitigationResponses = useSelector((state) => {
    if (!urlparams?.assessmentid) return {};
    return (
      state.capSelfAssessment.assessments[urlparams.assessmentid]
        ?.mitigationResponses || {}
    );
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleInputChange = (event, submissionID) => {
    const { value } = event.target;
    dispatch(
      saveMitigationResponse({
        assessmentId: urlparams.assessmentid,
        submissionId: submissionID,
        value,
      })
    );
  };

  const handleChangePage = (event, newPage) => {
    if (newPage > page) {
      mitigationNext();
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (rowId) => {
    setExpandedRow((prevRow) => (prevRow === rowId ? null : rowId));
  };

  // ── Data fetching ─────────────────────────────────────────────────────────
  const fetchPendingMitigation = async () => {
    setLoading(true);
    try {
      let params = { assessment_id: urlparams.assessmentid };
      const response = await fetchAssessmentReportDetailsforReviewService({ params });
      const controls = response.data?.controls || response.data || [];

      if (Array.isArray(controls) && controls.length > 0) {
        setAssessmentsList(controls);

        let mitigationItems = [];
        controls.forEach((e) => {
          e.subcontrols?.forEach((t) => {
            if (t.mitigationresponse) {
              mitigationItems[t.submissionID] = t.mitigationresponse.remarks;
            }
          });
          if (e.mitigation_rejection === 1) {
            setMitigationRejection(true);
          }
        });
        setMitigation(mitigationItems);
      }
    } catch (error) {
      openNotification("Error fetching pending assessments:", 'error');
    } finally {
      setLoading(false);
    }
  };

  const reviewMitigation = useCallback(async () => {
    try {
      let body = {
        assessment_id: urlparams.assessmentid,
        status: "Mitigation Response In Progress"
      };
      const response = await putAssessmentStatusService({ body });
      if (response.data !== '') {
        await fetchPendingMitigation();
        urlparams.status = "Mitigation Response In Progress";
      }
    } catch (error) {
      openNotification(' Server Error ', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (urlparams.status === 'Mitigation Initiated' || urlparams.status === 'Reject Mitigation Response') {
      reviewMitigation();
    } else {
      fetchPendingMitigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    pageValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentsList, page, rowsPerPage]);

  // ── Mitigation save / submit ──────────────────────────────────────────────
  const saveMitigationPlan = async (subcontrols) => {
    try {
      let updatedItemList = [];
      let lineItem;
      subcontrols.forEach((e) => {
        let remarks = mitigationResponses[e.submissionID]?.actionTaken;
        if (remarks && remarks.trim() !== '') {
          updatedItemList.push({ submissionId: e.submissionID, remarks });
        } else {
          lineItem = e;
        }
      });

      if (updatedItemList.length > 0) {
        let assessment_id = urlparams.assessmentid;
        if (assessment_id) {
          let response = await updateMultipleMitigationResponsesService({ updatedItemList });
          if (response.status === 201) {
            if (updatedItemList.length === subcontrols.length) {
              openNotification('Submitted successfully.', 'success');
              await fetchPendingMitigation();
            } else {
              openNotification('Please enter mitigation response for ref #' + lineItem.subcontrol, 'error');
            }
          }
        }
      } else {
        openNotification('Please enter mitigation response for ref #' + lineItem.subcontrol, 'error');
      }
    } catch (error) {
      openNotification('Error while updating mitigation response', 'error');
    }
  };

  const mitigationSubmit = async () => {
    setLoading(true);
    try {
      let isComplete = true;
      assessmentsList.forEach((e) => {
        e.subcontrols.forEach((t) => {
          if (t.mitigationresponse === undefined) {
            isComplete = false;
            openNotification('Please enter mitigation response for ref #' + t.subcontrol, 'error');
            setLoading(false);
            return false;
          }
        });
      });

      if (isComplete) {
        const body = { assessment_id: urlparams.assessmentid, status: 'Mitigation Response Submitted' };
        const response = await putAssessmentStatusService({ body });
        if (response.data !== '') {
          dispatch(clearMitigationResponse(urlparams.assessmentid));
          navigate('/cap/mitigationsuccess', {
            state: { assessmentid: urlparams.assessmentid, standard: urlparams.standard, status: 'Mitigation Response Submitted' }
          });
        }
      }
    } catch (error) {
      openNotification('Error while updating assessment status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const mitigationNext = async () => {
    try {
      await validatePageData();
    } catch (error) {
      openNotification('Error while validating', 'error');
    }
  };

  const validatePageData = async () => {
    setLoading(true);
    const length = Math.ceil(rowsPerPage * (page + 1));
    let isProceed = true;
    for (let i = 0; i < length; i++) {
      for (let j = 0; j < assessmentsList[i].subcontrols.length; j++) {
        let remarks = mitigationResponses[assessmentsList[i].subcontrols[j].submissionID]?.actionTaken;
        if (remarks === undefined || remarks.trim() === '') {
          openNotification('Please enter mitigation response for ref #' + assessmentsList[i].subcontrols[j].subcontrol, 'error');
          isProceed = false;
          setLoading(false);
          return false;
        }
      }
    }
    if (isProceed === true) {
      setPage(page + 1);
      pageValidate();
    } else {
      setPage(page);
    }
    setLoading(false);
  };

  const pageValidate = () => {
    const tableLength = rowsPerPage * (page + 1);
    const remainingLength = assessmentsList.length - 1;
    setIsSubmit(remainingLength <= tableLength);
  };

  // US-CAP-302: Evidence upload used to live here (mitigation-response stage).
  // It now happens exclusively at the self-assessment stage (SelfAssessmentPage) —
  // this page just displays what was already attached, read-only.

  return (
    <div>
      {/* ── Page header ── */}
      <div className="row py-1">
        <p>
          <img className="px-1 icons-style" src="../assets/icons/003-clipboard.svg" alt="View" />
          <b className="px-1"> Mitigation response submit</b>
          <img className="px-1 icons-style" src="../assets/icons/right-layer.svg" alt="View" />
          {urlparams.standard === 'CIS' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cis.png" alt="View" draggable="false" />}
          {urlparams.standard === 'ISO-27001' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-iso-27001.png" alt="View" draggable="false" />}
          {urlparams.standard === 'NIST' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nist.png" alt="View" draggable="false" />}
          {urlparams.standard === 'COBIT' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-cobit.png" alt="View" draggable="false" />}
          {urlparams.standard === 'TISAX' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-tisax.png" alt="View" draggable="false" />}
          {urlparams.standard === 'NIS 2.0' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-nis2.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Ransomware' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-ransomware.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Data Protection' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-data-protection.png" alt="View" draggable="false" />}
          {urlparams.standard === 'Supplier Risk' && <img className="px-sm standard-icon-size" src="../assets/icons/standard-supplier-risk.png" alt="View" draggable="false" />}
          <span> {urlparams.standard} </span> - <span>{urlparams.assessmentid}</span>
        </p>
      </div>

      {loading && <LoadingOverlay />}

      <div>
        {assessmentsList.length > 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>Control name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? assessmentsList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : assessmentsList
                ).map((item) => (
                  <React.Fragment key={item.controlID}>

                    {/* ── Outer control row ── */}
                    <TableRow>
                      <TableCell>
                        <IconButton
                          className="expand-row"
                          size="small"
                          onClick={() => handleRowClick(item.controlID)}
                        >
                          {expandedRow === item.controlID ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.controlID + ' : ' + item.controlName}</TableCell>
                      <TableCell>{item.assessment_id}</TableCell>
                    </TableRow>

                    {/* ── Expanded subcontrols row ── */}
                    <TableRow>
                      <TableCell colSpan={3} sx={{ p: 0 }}>
                        <Collapse in={expandedRow === item.controlID} timeout="auto" unmountOnExit>
                          <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: "6%" }}>Ref</TableCell>
                                <TableCell sx={{ width: "18%" }}>Control Question</TableCell>
                                <TableCell sx={{ width: "15%" }}>Answer Given</TableCell>
                                <TableCell sx={{ width: "16%" }}>Mitigation Suggestion</TableCell>
                                {mitigationRejection && (
                                  <TableCell sx={{ width: "13%" }}>Reject Remarks</TableCell>
                                )}
                                <TableCell sx={{ width: mitigationRejection ? "18%" : "25%" }}>Mitigation Response</TableCell>
                                <TableCell sx={{ width: "10%" }}>Evidence</TableCell>
                                <TableCell align="center" sx={{ width: "10%" }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(item.subcontrols || []).map((subcontrol) => {
                                const qType = subcontrol.question_type || "five_option";
                                return (
                                  <TableRow key={subcontrol.subcontrolID}>

                                    {/* Ref */}
                                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                                      {subcontrol.subcontrol}
                                    </TableCell>

                                    {/* Control Question */}
                                    <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}>
                                      <div className="subcontrols">{subcontrol.subcontrolName}</div>
                                    </TableCell>

                                    {/* Answer Given — QuestionRenderer read-only */}
                                    <TableCell sx={{ verticalAlign: "top" }}>
                                      <QuestionRenderer
                                        question_type={qType}
                                        answer_config={subcontrol.answer_config || null}
                                        value={subcontrol.answer_value || subcontrol.severity}
                                        readOnly={true}
                                      />
                                    </TableCell>

                                    {/* Mitigation Suggestion — read-only display */}
                                    <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                                      className="subcontrols">
                                      {subcontrol.mitigation}
                                    </TableCell>

                                    {/* Reject Remarks — only when mitigationRejection */}
                                    {mitigationRejection && (
                                      <TableCell sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                                        className="subcontrols">
                                        {subcontrol.comments !== undefined && subcontrol.comments}
                                      </TableCell>
                                    )}

                                    {/* Mitigation Response textarea */}
                                    <TableCell sx={{ verticalAlign: "top" }}>
                                      <textarea
                                        key={subcontrol.submissionID}
                                        id={subcontrol.submissionID}
                                        className="mit1"
                                        value={mitigationResponses[subcontrol.submissionID]?.actionTaken || ''}
                                        onChange={(e) => handleInputChange(e, subcontrol.submissionID)}
                                        placeholder="Mitigation Response"
                                        style={{ width: "100%", minHeight: "80px", resize: "vertical" }}
                                      />
                                    </TableCell>

                                    {/* Evidence — US-CAP-302: upload now happens at the SELF-ASSESSMENT
                                        stage only (see SelfAssessmentPage). This column is read-only here,
                                        showing what was already attached for reviewer/mitigation context. */}
                                    <TableCell sx={{ verticalAlign: "top" }}>
                                      <div>
                                        {(subcontrol.evidence_files || []).length > 0 ? (
                                          (subcontrol.evidence_files || []).map((file, idx) => (
                                            <div key={file.key || idx} style={{ fontSize: 12, marginBottom: 4 }}>
                                              📎 {file.filename}
                                            </div>
                                          ))
                                        ) : (
                                          <span style={{ fontSize: 12, color: '#888' }}>No evidence attached</span>
                                        )}
                                        {subcontrol.mitigationresponse !== undefined &&
                                          subcontrol.mitigationresponse.evidence !== undefined && (
                                            <div>
                                              <a className='text-primary' href={subcontrol.mitigationresponse.evidence}
                                                target="_blank" rel="noreferrer">
                                                Download previous mitigation evidence
                                              </a>
                                            </div>
                                          )}
                                      </div>
                                    </TableCell>

                                    {/* Actions — View Details button */}
                                    <TableCell align="center" sx={{ verticalAlign: "top" }}>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<InfoOutlinedIcon />}
                                        onClick={() => openDetailDialog(subcontrol)}
                                        sx={{
                                          textTransform: "none",
                                          fontSize: "12px",
                                          whiteSpace: "nowrap",
                                          borderColor: "#1976d2",
                                          color: "#1976d2",
                                          "&:hover": { backgroundColor: "#e3f2fd" },
                                        }}
                                      >
                                        View Details
                                      </Button>
                                    </TableCell>

                                  </TableRow>
                                );
                              })}

                              {/* Save row */}
                              <TableRow>
                                <TableCell colSpan={mitigationRejection ? 8 : 7} className="txt-c">
                                  <button
                                    className="approve-button"
                                    style={{ width: '80px' }}
                                    type="button"
                                    onClick={() => saveMitigationPlan(item.subcontrols)}
                                  >
                                    Save
                                  </button>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              component="div"
              count={assessmentsList.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}

        {/* ── Action buttons ── */}
        {assessmentsList.length > 0 && (
          <div className='assessments-list'>
            {isSubmit ? (
              <button className="approve-button w-150" onClick={() => mitigationSubmit()}>
                Submit
              </button>
            ) : (
              <button className="approve-button w-150" onClick={() => mitigationNext()}>
                Next
              </button>
            )}
            <button className="go-back-button w-150" onClick={() => window.history.back()}>
              <ArrowLeftIcon /> Go back
            </button>
          </div>
        )}
      </div>

      {/* ── View Details dialog ── */}
      {detailDialog && selectedSubcontrol && (
        <Dialog
          open={detailDialog}
          onClose={closeDetailDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "10px" } }}
        >
          <DialogTitle sx={{ m: 0, p: 2, backgroundColor: "#1a2b5e", color: "#fff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InfoOutlinedIcon sx={{ fontSize: 20 }} />
              <span style={{ fontSize: "15px", fontWeight: 600 }}>
                {selectedSubcontrol.subcontrol} — Question Details
              </span>
            </div>
            <IconButton
              aria-label="close"
              onClick={closeDetailDialog}
              sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 3 }}>

            {/* Control Question */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>
                Control Question
              </div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a2b5e" }}>
                {selectedSubcontrol.subcontrolName}
              </div>
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Answer Given */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                Answer Given
              </div>
              <QuestionRenderer
                question_type={selectedSubcontrol.question_type || "five_option"}
                answer_config={selectedSubcontrol.answer_config || null}
                value={selectedSubcontrol.answer_value || selectedSubcontrol.severity}
                readOnly={true}
              />
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Rationale */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                Rationale for Rating
              </div>
              <div style={{
                background: "#f8f9fa", border: "1px solid #dee2e6", borderRadius: "6px",
                padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                color: selectedSubcontrol.rational ? "#333" : "#aaa",
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {selectedSubcontrol.rational || "No rationale provided"}
              </div>
            </div>

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Mitigation Suggestion */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                Mitigation Suggestion
              </div>
              <div style={{
                background: "#f0f7ff", border: "1px solid #b3d4f5", borderRadius: "6px",
                padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                color: selectedSubcontrol.mitigation ? "#1a2b5e" : "#aaa",
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {selectedSubcontrol.mitigation || "No mitigation suggestion provided"}
              </div>
            </div>

            {/* Reject Remarks — only if present */}
            {mitigationRejection && selectedSubcontrol.comments && (
              <>
                <hr style={{ margin: "12px 0", borderColor: "#eee" }} />
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontSize: "11px", color: "#B60000", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                    Reject Remarks
                  </div>
                  <div style={{
                    background: "#fff5f5", border: "1px solid #ffcccc", borderRadius: "6px",
                    padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                    color: "#B60000", whiteSpace: "pre-wrap", wordBreak: "break-word",
                  }}>
                    {selectedSubcontrol.comments}
                  </div>
                </div>
              </>
            )}

            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

            {/* Mitigation Response — live from Redux */}
            <div>
              <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
                Mitigation Response
              </div>
              <div style={{
                background: "#f0fff4", border: "1px solid #b3e6c8", borderRadius: "6px",
                padding: "10px 14px", fontSize: "13px", lineHeight: "1.6",
                color: mitigationResponses[selectedSubcontrol.submissionID]?.actionTaken ? "#1a5e2b" : "#aaa",
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {mitigationResponses[selectedSubcontrol.submissionID]?.actionTaken || "No Mitigation Response entered yet"}
              </div>
            </div>

          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SubmitMitigationResponsePage;